// src/hooks/useSessionLimit.js
import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const FREE_SESSION_LIMIT = 4;

export const useSessionLimit = (userId) => {
  const [loading, setLoading] = useState(true);
  const [sessionCount, setSessionCount] = useState(0);
  const [isPremium, setIsPremium] = useState(false);
  const [canCreateSession, setCanCreateSession] = useState(true);
  const [limitReached, setLimitReached] = useState(false);
  const [limitEnabled, setLimitEnabled] = useState(true); // NEW

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    checkSessionLimit();
  }, [userId]);

  const checkSessionLimit = async () => {
    try {
      setLoading(true);
      console.log('🔍 Checking session limit for user:', userId);

      // ✅ NEW: Check if admin has enabled the free session limit
      const { data: settingData } = await supabase
        .from('admin_settings')
        .select('setting_value')
        .eq('setting_key', 'free_session_limit_enabled')
        .single();

      const isLimitEnabled = settingData?.setting_value === true || settingData?.setting_value === 'true';
      setLimitEnabled(isLimitEnabled);
      console.log('⚙️ Free session limit enabled:', isLimitEnabled);

      // ✅ If admin disabled the limit, everyone has unlimited sessions
      if (!isLimitEnabled) {
        console.log('✅ Session limit disabled by admin - unlimited access for all');
        setCanCreateSession(true);
        setLimitReached(false);
        setLoading(false);
        return;
      }

      // Check if user is premium
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('is_premium')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Profile error:', profileError);
        setIsPremium(false);
      } else {
        const userIsPremium = profile?.is_premium || false;
        setIsPremium(userIsPremium);
        console.log('💎 Premium status:', userIsPremium);

        // If premium, no limits
        if (userIsPremium) {
          setCanCreateSession(true);
          setLimitReached(false);
          setLoading(false);
          return;
        }
      }

      // Count user's sessions
      const { count, error: countError } = await supabase
        .from('daily_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (countError) {
        console.error('Count error:', countError);
        throw countError;
      }

      const sessionTotal = count || 0;
      setSessionCount(sessionTotal);
      
      const hasReachedLimit = sessionTotal >= FREE_SESSION_LIMIT;
      setLimitReached(hasReachedLimit);
      setCanCreateSession(!hasReachedLimit);

      console.log(`📊 Sessions: ${sessionTotal}/${FREE_SESSION_LIMIT} | Can create: ${!hasReachedLimit}`);

    } catch (error) {
      console.error('❌ Error checking session limit:', error);
      // On error, allow session creation (fail open)
      setCanCreateSession(true);
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    if (userId) {
      checkSessionLimit();
    }
  };

  return {
    loading,
    sessionCount,
    isPremium,
    canCreateSession,
    limitReached,
    limitEnabled, // NEW: Expose this to components
    sessionsRemaining: Math.max(0, FREE_SESSION_LIMIT - sessionCount),
    refresh,
    FREE_SESSION_LIMIT
  };
};